import React, { useCallback, useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";

import { Checkbox, FormControlLabel } from "@material-ui/core";
import { Check as CheckIcon, Close as CloseIcon } from "@material-ui/icons";
import {
  Banner,
  Button,
  ButtonGroup,
  Card,
  DataTable,
  Layout,
  Modal,
  Select,
  TextField,
} from "@shopify/polaris";

import { useAdmin } from "../../context/admin";
import { useUser } from "../../context/user";
import { deepEqual } from "../../util/helpers";
import ModalEditWindow from "./ModalEditWindow";

const formatHourLabel = (hour) => {
  const period = hour >= 12 ? "pm" : "am";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${period}`;
};

const TIME_OPTIONS = Array.from({ length: 24 }).map((_, hour) => ({
  label: formatHourLabel(hour),
  value: `${hour.toString().padStart(2, "0")}:00`,
  hour,
}));

const TIME_SELECT_OPTIONS = TIME_OPTIONS.map(({ label, value }) => ({
  label,
  value,
}));

const findTimeOption = (value) =>
  TIME_OPTIONS.find((option) => option.value === value);

const collectAllWindows = (target) => {
  if (!target) {
    return [];
  }

  if (target.pickup || target.delivery) {
    return [
      ...collectAllWindows(target.pickup),
      ...collectAllWindows(target.delivery),
    ];
  }

  return Object.values(target || {}).flatMap((day) => day?.windows || []);
};

const nextCustomWindowId = (schedules) => {
  const highest = collectAllWindows(schedules).reduce((max, window) => {
    const { id } = window || {};
    if (typeof id === "string" && id.startsWith("CW-")) {
      const [, suffix] = id.split("-");
      const num = parseInt(suffix, 10);
      if (!Number.isNaN(num) && num > max) {
        return num;
      }
    }
    return max;
  }, 0);
  return `CW-${highest + 1}`;
};

const normalizeSchedule = (scheduleData) => {
  if (!scheduleData || Array.isArray(scheduleData)) {
    return {};
  }
  return scheduleData;
};

export default function WeeklySchedule({
  pickupSchedule,
  deliverySchedule,
  handleScheduleChange,
  saving,
  availableWindows = [],
}) {
  const [activeType, setActiveType] = useState("pickup");
  const [forms, setForms] = useState({
    pickup: normalizeSchedule(pickupSchedule),
    delivery: normalizeSchedule(deliverySchedule),
  });
  const [editingWindow, setEditingWindow] = useState(false);
  const [manageWindowsDay, setManageWindowsDay] = useState(null);
  const [manageWindowSelection, setManageWindowSelection] = useState("");
  const [newWindowForm, setNewWindowForm] = useState({
    start: "",
    end: "",
    limit: "0",
  });
  const [newWindowError, setNewWindowError] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const user = useUser();
  const admin = useAdmin();
  const canEditWindows = Boolean(user?.isAdmin || user?.isManager);
  const shopConfig = admin.selectedShop?.shop_config || {};
  const [syncWindows, setSyncWindows] = useState(
    shopConfig.sync_schedule_windows !== false
  );

  // Get shop locations and location_settings
  const locations = admin.selectedShop?.locations || [];
  const locationSettings = shopConfig.location_settings || {};

  // Build location options for the selector
  const locationOptions = useMemo(() => {
    const options = [{ label: "All Locations (Shop Default)", value: "" }];
    locations
      .filter((loc) => loc.active)
      .forEach((loc) => {
        options.push({
          label: loc.name,
          value: loc.id.toString(),
        });
      });
    return options;
  }, [locations]);

  // Get location-specific schedule if exists
  const getLocationSchedule = useCallback(
    (locId, type) => {
      if (!locId) return null;
      const locConfig = locationSettings[locId] || {};
      return type === "pickup"
        ? locConfig.pickup_schedule
        : locConfig.delivery_schedule;
    },
    [locationSettings]
  );

  // Check if location has custom schedule
  const locationHasCustomSchedule = useMemo(() => {
    if (!selectedLocationId) return false;
    const locConfig = locationSettings[selectedLocationId] || {};
    return !!(locConfig.pickup_schedule || locConfig.delivery_schedule);
  }, [selectedLocationId, locationSettings]);

  // Handle saving location-specific schedule
  const handleLocationScheduleSave = (scheduleData, type) => {
    if (!selectedLocationId) return;

    const scheduleKey =
      type === "pickup" ? "pickup_schedule" : "delivery_schedule";

    admin.updateShopConfig({
      location_settings: {
        ...locationSettings,
        [selectedLocationId]: {
          ...(locationSettings[selectedLocationId] || {}),
          [scheduleKey]: scheduleData,
        },
      },
    });
  };

  // Handle sync toggle
  const handleSyncToggle = (checked) => {
    setSyncWindows(checked);
    admin.updateShopConfig({ sync_schedule_windows: checked });
  };

  // Handle clearing location-specific schedule (revert to shop default)
  const handleClearLocationSchedule = () => {
    if (!selectedLocationId) return;

    const locConfig = { ...(locationSettings[selectedLocationId] || {}) };
    delete locConfig.pickup_schedule;
    delete locConfig.delivery_schedule;
    delete locConfig.window_limit;

    admin.updateShopConfig({
      location_settings: {
        ...locationSettings,
        [selectedLocationId]: locConfig,
      },
    });
  };

  const normalizedAvailableWindows = useMemo(() => availableWindows || [], [
    availableWindows,
  ]);

  const form = useMemo(() => forms[activeType] || {}, [forms, activeType]);

  // Load schedules - use location-specific if selected, otherwise shop default
  useEffect(() => {
    if (selectedLocationId) {
      // Load location-specific schedules, or copy from shop default if none exist
      const locPickup = getLocationSchedule(selectedLocationId, "pickup");
      const locDelivery = getLocationSchedule(selectedLocationId, "delivery");
      setForms({
        pickup: normalizeSchedule(locPickup || pickupSchedule),
        delivery: normalizeSchedule(locDelivery || deliverySchedule),
      });
    } else {
      // Load shop default schedules
      setForms({
        pickup: normalizeSchedule(pickupSchedule),
        delivery: normalizeSchedule(deliverySchedule),
      });
    }
  }, [pickupSchedule, deliverySchedule, selectedLocationId, getLocationSchedule]);

  useEffect(() => {
    if (!canEditWindows) {
      setManageWindowsDay(null);
      return;
    }

    if (manageWindowsDay) {
      const defaultLimit = `${form[manageWindowsDay]?.windows?.[0]?.limit ??
        0}`;
      setNewWindowForm({ start: "", end: "", limit: defaultLimit });
      setNewWindowError("");
      setManageWindowSelection("");
    } else {
      setManageWindowSelection("");
    }
  }, [manageWindowsDay, form, canEditWindows]);

  useEffect(() => {
    setManageWindowsDay(null);
    setEditingWindow(false);
  }, [activeType]);

  useEffect(() => {
    setSyncWindows(shopConfig.sync_schedule_windows !== false);
  }, [shopConfig.sync_schedule_windows]);

  const handleChange = (wkday, val) => {
    if (!canEditWindows) {
      return;
    }

    setForms((state) => {
      const current = state[activeType] || {};
      const updatedDay = {
        ...current[wkday],
        ...val,
      };

      if (syncWindows) {
        // Apply to both pickup and delivery
        return {
          pickup: {
            ...(state.pickup || {}),
            [wkday]: updatedDay,
          },
          delivery: {
            ...(state.delivery || {}),
            [wkday]: updatedDay,
          },
        };
      }

      return {
        ...state,
        [activeType]: {
          ...current,
          [wkday]: updatedDay,
        },
      };
    });
  };

  const handleToggleDayClosed = (wkday) => {
    if (!canEditWindows) {
      return;
    }

    const day = form[wkday] || {};
    handleChange(wkday, { closed: !day.closed });
  };

  const handleSave = () => {
    if (!canEditWindows) {
      return;
    }

    if (selectedLocationId) {
      // Save to location-specific settings
      if (syncWindows) {
        // Save to both pickup and delivery
        handleLocationScheduleSave({ ...form }, "pickup");
        handleLocationScheduleSave({ ...form }, "delivery");
      } else {
        handleLocationScheduleSave({ ...form }, activeType);
      }
    } else {
      // Save to shop default
      if (syncWindows) {
        // Save to both pickup and delivery
        handleScheduleChange({ ...form }, "pickup");
        handleScheduleChange({ ...form }, "delivery");
      } else {
        handleScheduleChange({ ...form }, activeType);
      }
    }
    setEditingWindow(false);
  };

  const handleChangeLimit = (wkday, limit) => {
    if (!canEditWindows) {
      return;
    }

    setForms((state) => {
      const current = state[activeType] || {};
      const day = current[wkday] || {};
      const updatedDay = {
        ...day,
        windows: (day.windows || []).map((w) => ({ ...w, limit })),
      };

      if (syncWindows) {
        // Apply to both pickup and delivery
        const pickupDay = state.pickup?.[wkday] || {};
        const deliveryDay = state.delivery?.[wkday] || {};
        return {
          pickup: {
            ...(state.pickup || {}),
            [wkday]: {
              ...pickupDay,
              windows: (pickupDay.windows || []).map((w) => ({ ...w, limit })),
            },
          },
          delivery: {
            ...(state.delivery || {}),
            [wkday]: {
              ...deliveryDay,
              windows: (deliveryDay.windows || []).map((w) => ({
                ...w,
                limit,
              })),
            },
          },
        };
      }

      return {
        ...state,
        [activeType]: {
          ...current,
          [wkday]: updatedDay,
        },
      };
    });
  };

  const handleChangeWindow = (wkday, window, index) => {
    if (!canEditWindows) {
      return;
    }

    setForms((state) => {
      const current = state[activeType] || {};
      const day = current[wkday] || {};
      const windows = (day.windows || []).map((w, idx) =>
        idx === index ? window : w
      );

      if (syncWindows) {
        // Apply to both pickup and delivery
        const pickupDay = state.pickup?.[wkday] || {};
        const deliveryDay = state.delivery?.[wkday] || {};
        const pickupWindows = (pickupDay.windows || []).map((w, idx) =>
          idx === index ? window : w
        );
        const deliveryWindows = (deliveryDay.windows || []).map((w, idx) =>
          idx === index ? window : w
        );
        return {
          pickup: {
            ...(state.pickup || {}),
            [wkday]: { ...pickupDay, windows: pickupWindows },
          },
          delivery: {
            ...(state.delivery || {}),
            [wkday]: { ...deliveryDay, windows: deliveryWindows },
          },
        };
      }

      return {
        ...state,
        [activeType]: {
          ...current,
          [wkday]: {
            ...day,
            windows,
          },
        },
      };
    });
  };

  const handleRemoveWindow = (wkday, targetIndex) => {
    if (!canEditWindows) {
      return;
    }

    setForms((state) => {
      const current = state[activeType] || {};
      const day = current[wkday] || {};
      const windows = (day.windows || []).filter(
        (_, idx) => idx !== targetIndex
      );
      return {
        ...state,
        [activeType]: {
          ...current,
          [wkday]: {
            ...day,
            windows,
          },
        },
      };
    });
  };

  const handleAddWindow = (wkday, identifier) => {
    if (!canEditWindows || !identifier) {
      return false;
    }

    const windowToAdd = normalizedAvailableWindows.find(
      (w) => (w.id || w.code) === identifier
    );
    if (!windowToAdd) {
      return false;
    }

    const currentDay = (forms[activeType] || {})[wkday] || {};
    const currentWindows = currentDay.windows || [];
    if (
      currentWindows.some(
        (w) => (w.id || w.code) === (windowToAdd.id || windowToAdd.code)
      )
    ) {
      return false;
    }

    const defaultLimit = currentWindows[0]?.limit ?? windowToAdd.limit ?? 0;
    const newWindows = [
      ...currentWindows,
      { ...windowToAdd, active: true, limit: defaultLimit },
    ].sort((a, b) => (a.collection_time ?? 0) - (b.collection_time ?? 0));

    setForms((state) => {
      const current = state[activeType] || {};
      const day = current[wkday] || {};
      return {
        ...state,
        [activeType]: {
          ...current,
          [wkday]: {
            ...day,
            windows: newWindows,
          },
        },
      };
    });

    return true;
  };

  const updateNewWindowForm = (field, value) => {
    setNewWindowForm((state) => ({
      ...state,
      [field]: value,
    }));
    setNewWindowError("");
  };

  const handleCreateCustomWindow = () => {
    if (!canEditWindows || !manageWindowsDay) {
      return;
    }

    const { start, end, limit } = newWindowForm;
    const startOption = findTimeOption(start);
    const endOption = findTimeOption(end);

    if (!startOption || !endOption) {
      setNewWindowError("Please select start and end times.");
      return;
    }

    if (startOption.hour >= endOption.hour) {
      setNewWindowError("Start time must be before end time.");
      return;
    }

    const numericLimit = Number(limit);
    const safeLimit =
      Number.isFinite(numericLimit) && numericLimit >= 0 ? numericLimit : 0;

    const baseId = nextCustomWindowId(forms);
    const newWindow = {
      id: baseId,
      code: baseId,
      name: `${startOption.label} - ${endOption.label}`,
      collection_time: startOption.hour,
      due_time: endOption.hour,
      active: true,
      limit: safeLimit,
    };

    const existingWindows = form[manageWindowsDay]?.windows || [];
    handleChange(manageWindowsDay, {
      windows: [...existingWindows, newWindow],
    });

    setNewWindowForm((state) => ({
      ...state,
      start: "",
      end: "",
    }));
    setNewWindowError("");
  };

  const headings = ["Open", "Day", "Windows", "Limit Per Window"];

  const rows = Object.keys(form || {}).map((wkday) => {
    const day = form[wkday] || {};
    const windows = day.windows || [];

    const windowContent = (
      <div style={{ width: "100%" }}>
        {day.closed ? (
          <div>Shop is closed.</div>
        ) : windows.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "1.15rem",
              width: "100%",
            }}
          >
            {windows.map((window, index) => {
              const parts = [
                wkday,
                window.id,
                window.code,
                window.collection_time,
                window.due_time,
                index,
              ].filter(
                (value) =>
                  value !== undefined && value !== null && `${value}`.length > 0
              );
              const key = parts.join("-");
              return (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                  }}
                >
                  <Checkbox
                    disabled={saving || !canEditWindows}
                    checked={window.active}
                    onChange={() =>
                      handleChangeWindow(
                        wkday,
                        {
                          ...window,
                          active: !window.active,
                        },
                        index
                      )
                    }
                    name={day.label}
                    color="primary"
                  />
                  <span
                    style={{
                      cursor: canEditWindows ? "pointer" : "default",
                      flexGrow: 1,
                      minWidth: 0,
                    }}
                    onClick={() => {
                      if (!canEditWindows) {
                        return;
                      }
                      setEditingWindow({
                        date: wkday,
                        day: form[wkday],
                        window,
                        index,
                      });
                    }}
                  >
                    {window.name}
                  </span>
                  {!window.active ? (
                    <span style={{ color: "#637381", fontSize: "0.85rem" }}>
                      (disabled)
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div>No service windows configured.</div>
        )}
        {canEditWindows ? (
          <div style={{ marginTop: "0.5rem" }}>
            <Button size="slim" onClick={() => setManageWindowsDay(wkday)}>
              Edit windows
            </Button>
          </div>
        ) : null}
      </div>
    );

    return [
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Checkbox
          disabled={saving}
          checked={day.closed}
          tabIndex={-1}
          checkedIcon={<CloseIcon />}
          icon={<CheckIcon />}
          onClick={() => handleToggleDayClosed(wkday)}
        />
      </div>,
      <div style={{ display: "flex", alignItems: "center" }}>{day.label}</div>,
      <div style={{ width: "100%" }}>{windowContent}</div>,
      <div style={{ display: "flex", alignItems: "center", maxWidth: "200px" }}>
        <TextField
          type="number"
          step={5}
          min={0}
          value={`${windows.length > 0 ? windows[0].limit : 0}`}
          disabled={saving || windows.length === 0 || day.closed}
          onChange={(val) => handleChangeLimit(wkday, +val)}
        />
      </div>,
    ];
  });

  const originalForm =
    activeType === "pickup"
      ? normalizeSchedule(pickupSchedule)
      : normalizeSchedule(deliverySchedule);
  const disabled = saving || deepEqual(form, originalForm);

  return (
    <Layout.AnnotatedSection
      title="Weekly Schedule"
      description="Customize store pick up and delivery windows. Close store on certain days or disable specific windows to suit your needs."
    >
      <Card
        primaryFooterAction={{
          content:
            activeType === "pickup"
              ? "Save Pickup Schedule"
              : "Save Delivery Schedule",
          loading: saving,
          onAction: handleSave,
          disabled,
        }}
      >
        <Card.Section>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <ButtonGroup segmented>
              <Button
                pressed={activeType === "pickup"}
                onClick={() => setActiveType("pickup")}
              >
                Pickup Schedule
              </Button>
              <Button
                pressed={activeType === "delivery"}
                onClick={() => setActiveType("delivery")}
              >
                Delivery Schedule
              </Button>
            </ButtonGroup>
            {locations.length > 1 && (
              <div style={{ minWidth: "200px" }}>
                <Select
                  label="Location"
                  labelInline
                  options={locationOptions}
                  value={selectedLocationId}
                  onChange={setSelectedLocationId}
                />
              </div>
            )}
          </div>
          <div style={{ marginTop: "1rem" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncWindows}
                  onChange={(e) => handleSyncToggle(e.target.checked)}
                  color="primary"
                />
              }
              label="Sync pickup and delivery windows (changes apply to both)"
            />
          </div>
        </Card.Section>
        {selectedLocationId && (
          <Card.Section>
            <Banner
              status={locationHasCustomSchedule ? "warning" : "info"}
              action={
                locationHasCustomSchedule
                  ? {
                      content: "Reset to Shop Default",
                      onAction: handleClearLocationSchedule,
                    }
                  : undefined
              }
            >
              <p>
                <strong>
                  {locationHasCustomSchedule
                    ? "Editing custom schedule for"
                    : "Creating custom schedule for"}
                </strong>{" "}
                <strong>
                  {locations.find((l) => l.id.toString() === selectedLocationId)
                    ?.name || "this location"}
                </strong>
                .{" "}
                {locationHasCustomSchedule
                  ? "This location has its own windows and limits that override the shop default."
                  : "Changes will create a location-specific schedule. Orders assigned to this location will use these windows instead of the shop default."}
              </p>
            </Banner>
          </Card.Section>
        )}
        <Card.Section>
          <DataTable
            columnContentTypes={["text", "text", "text", "text"]}
            headings={headings}
            rows={rows}
          />
        </Card.Section>
      </Card>

      <Modal
        title={
          manageWindowsDay
            ? `Manage windows - ${form[manageWindowsDay].label}`
            : ""
        }
        open={!!manageWindowsDay}
        onClose={() => setManageWindowsDay(null)}
        secondaryActions={[
          { content: "Cancel", onAction: () => setManageWindowsDay(null) },
        ]}
        primaryAction={{
          content: "Done",
          onAction: () => setManageWindowsDay(null),
        }}
      >
        <Modal.Section>
          {manageWindowsDay ? (
            <>
              {(form[manageWindowsDay].windows || []).length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  {form[manageWindowsDay].windows.map((window, index) => (
                    <div
                      key={window.id || window.code || index}
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "center",
                      }}
                    >
                      <Checkbox
                        disabled={saving}
                        checked={window.active}
                        onChange={() =>
                          handleChangeWindow(
                            manageWindowsDay,
                            {
                              ...window,
                              active: !window.active,
                            },
                            index
                          )
                        }
                        name={form[manageWindowsDay].label}
                        color="primary"
                      />
                      <span
                        style={{ cursor: "pointer", flexGrow: 1 }}
                        onClick={() =>
                          setEditingWindow({
                            date: manageWindowsDay,
                            day: form[manageWindowsDay],
                            window,
                            index,
                          })
                        }
                      >
                        {window.name}
                      </span>
                      <Button
                        size="slim"
                        destructive
                        onClick={() =>
                          handleRemoveWindow(manageWindowsDay, index)
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div>No service windows configured.</div>
              )}
              {canEditWindows
                ? (() => {
                    const existingIdentifiers = (
                      form[manageWindowsDay].windows || []
                    ).map((w) => w.id || w.code);
                    const options = normalizedAvailableWindows
                      .filter((window) => {
                        const identifier = window.id || window.code;
                        return existingIdentifiers.indexOf(identifier) === -1;
                      })
                      .map((window) => ({
                        label: window.name,
                        value: window.id || window.code,
                      }));

                    const startOption = findTimeOption(newWindowForm.start);
                    const endOption = findTimeOption(newWindowForm.end);
                    const addDisabled =
                      !startOption ||
                      !endOption ||
                      startOption.hour >= endOption.hour;

                    return (
                      <div
                        style={{
                          marginTop: "1rem",
                          display: "flex",
                          flexDirection: "column",
                          gap: "1rem",
                        }}
                      >
                        {normalizedAvailableWindows.length > 0 ? (
                          options.length > 0 ? (
                            <Select
                              label="Add existing service window"
                              options={[
                                {
                                  label: "Select a service window",
                                  value: "",
                                },
                                ...options,
                              ]}
                              value={manageWindowSelection}
                              onChange={(value) => {
                                setManageWindowSelection(value);
                                if (handleAddWindow(manageWindowsDay, value)) {
                                  setManageWindowSelection("");
                                }
                              }}
                            />
                          ) : (
                            <div style={{ color: "#637381" }}>
                              All available service windows have been added.
                            </div>
                          )
                        ) : null}

                        <div
                          style={{
                            display: "grid",
                            gap: "0.75rem",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(160px, 1fr))",
                          }}
                        >
                          <Select
                            label="Start time"
                            options={[
                              { label: "Select start time", value: "" },
                              ...TIME_SELECT_OPTIONS,
                            ]}
                            value={newWindowForm.start}
                            onChange={(value) =>
                              updateNewWindowForm("start", value)
                            }
                          />
                          <Select
                            label="End time"
                            options={[
                              { label: "Select end time", value: "" },
                              ...TIME_SELECT_OPTIONS,
                            ]}
                            value={newWindowForm.end}
                            onChange={(value) =>
                              updateNewWindowForm("end", value)
                            }
                          />
                          <TextField
                            label="Limit"
                            type="number"
                            min={0}
                            value={newWindowForm.limit}
                            onChange={(value) =>
                              updateNewWindowForm("limit", value)
                            }
                          />
                        </div>

                        <div>
                          <Button
                            primary
                            onClick={handleCreateCustomWindow}
                            disabled={addDisabled}
                          >
                            Add custom window
                          </Button>
                          {newWindowError ? (
                            <div
                              style={{
                                color: "#d82c0d",
                                marginTop: "0.5rem",
                              }}
                            >
                              {newWindowError}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()
                : null}
            </>
          ) : null}
        </Modal.Section>
      </Modal>

      <ModalEditWindow
        open={!!editingWindow}
        editingWindow={editingWindow}
        setEditingWindow={setEditingWindow}
        saving={saving}
        onClose={() => setEditingWindow(false)}
        onSave={() => {
          handleChangeWindow(
            editingWindow.date,
            { ...editingWindow.window },
            editingWindow.index
          );
          setEditingWindow(false);
        }}
      />
    </Layout.AnnotatedSection>
  );
}

WeeklySchedule.propTypes = {
  pickupSchedule: PropTypes.object,
  deliverySchedule: PropTypes.object,
  handleScheduleChange: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  availableWindows: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    })
  ),
};
